import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Col, Form, Row } from "react-bootstrap";
import { post } from "../../../helpers/api_helper";
import { Spin } from "antd";
import {
  successMessage,
  errorMessage,
  capitalizeString,
  warningMessage,
  delete_RecordWithoutApi,
} from "../CommonFunctions";

import CustomSelect from "../../../CustomControls/CustomSelect";
import { SVGICON } from "../../../constant/theme";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import moment from "moment";
import {
  POST_GetNDTMaterialDetailByID,
  POST_NDTMaterialInsert,
  POST_NDTMaterialUpdate,
} from "../../../helpers/url_helper";

const AddNDTMaterial = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { PrimaryIdParam } = useParams();
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const ndtProcessTypeOptions = [
    { label: "UT", value: 1 },
    { label: "RT", value: 2 },
    { label: "MT", value: 3 },
    { label: "PT", value: 4 },
  ];

  useEffect(() => {
    if (PrimaryIdParam) {
      getNDTMaterialDetailByID();
    }
  }, []);

  const getNDTMaterialDetailByID = () => {
    setIsLoading(true);
    post(POST_GetNDTMaterialDetailByID, {
      NDTMaterialIDEncrypt: window.atob(PrimaryIdParam),
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setValues({
            ...response,
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const validationSchema = Yup.object().shape({
    MaterialName: Yup.string()
      .trim()
      .required("Material Name is required field"),
    Manufacturer: Yup.string()
      .trim()
      .required("Manufacturer  is required field"),
    NDTProcessType: Yup.number()
      .required("NDT Process Type is required field")
      .min(1, "NDT Process Type is required field"),
    BatchNo: Yup.string().trim().required("Batch No is required field"),
    ManufacturerDate: Yup.string()
      .trim()
      .required("Manufacturer Date is required field"),
  });

  const formik = useFormik({
    validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      NDTMaterialIDEncrypt: PrimaryIdParam ? window.atob(PrimaryIdParam) : "",
      MaterialName: "",
      NDTProcessType: 0,
      Manufacturer: "",
      BatchNo: "",
      ManufacturerDate: "",
      ExpiryDate: "",
    },
    validateOnBlur: false,
  });

  const {
    values: { NDTMaterialIDEncrypt, ManufacturerDate },
  } = formik;

  const handleValidSubmit = (values) => {
    if (values?.ExpiryDate != "") {
      if (values?.ManufacturerDate > values?.ExpiryDate) {
        warningMessage("Manufacturer Date cannot be greater than Expiry Date.");
        return;
      }
    }

    setIsLoading(true);
    const payload = {
      ...values,
      ManufacturerDate: values?.ManufacturerDate
        ? moment(values?.ManufacturerDate)
            .format("YYYY-MM-DD")
            .replace(/^\-/, "")
        : "",
      ExpiryDate: values?.ExpiryDate
        ? moment(values?.ExpiryDate).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
      CreatedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ModifiedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
    };
    post(
      PrimaryIdParam ? POST_NDTMaterialUpdate : POST_NDTMaterialInsert,
      payload
    )
      .then((response) => {
        if (response?.IsSuccess) {
          successMessage(response?.Message);
          setTimeout(() => {
            navigate("/ndtmaterial");
          }, 1000);
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  return (
    <>
      <div className="card m-1">
        <div className="card-body p-1">
          <Spin
            size="large"
            spinning={isLoading}
            tip={"Loading..."}
            style={{ position: "fixed" }}
          >
            <div className="tbl-caption p-2">
              <h4 className="heading mb-0">
                {PrimaryIdParam ? "Edit" : "Add"} NDT Material
              </h4>
            </div>
            <hr className="mt-0 mb-1"></hr>
            <div className="p-2 pt-0">
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col lg={3}>
                      <Field
                        label="NDT Process Type"
                        name="NDTProcessType"
                        placeholder={"Select NDT Process Type"}
                        component={CustomSelect}
                        options={ndtProcessTypeOptions}
                        required
                      />
                    </Col>
                    <Col lg={3}>
                      <Field
                        type="text"
                        label="Material Name"
                        name="MaterialName"
                        placeholder={`Enter Material Name`}
                        component={CustomInput}
                        maxLength={250}
                        required
                        autoFocus
                        onChange={(e) => {
                          formik.setFieldValue(
                            "MaterialName",
                            capitalizeString(e)
                          );
                        }}
                      />
                    </Col>
                    <Col lg={3}>
                      <Field
                        type="text"
                        label="Manufacturer"
                        name="Manufacturer"
                        placeholder={`Enter Manufacturer`}
                        component={CustomInput}
                        maxLength={250}
                        required
                        onChange={(e) => {
                          formik.setFieldValue(
                            "Manufacturer",
                            capitalizeString(e)
                          );
                        }}
                      />
                    </Col>
                    <Col lg={3}>
                      <Field
                        type="text"
                        label="Batch No"
                        name="BatchNo"
                        placeholder={`Enter Batch No`}
                        component={CustomInput}
                        maxLength={250}
                        required
                        onChange={(e) => {
                          formik.setFieldValue("BatchNo", capitalizeString(e));
                        }}
                      />
                    </Col>
                    <Col lg={3}>
                      <Field
                        label="Manufacturer Date"
                        name={"ManufacturerDate"}
                        placeholder={"Select Manufacturer Date"}
                        component={CustomFlatpickr}
                        options={{
                          altInput: true,
                          altFormat: "j-M-Y",
                          dateFormat: "d-m-Y",
                        }}
                        required
                      />
                    </Col>
                    <Col lg={3}>
                      <Field
                        label="Expiry Date"
                        name={"ExpiryDate"}
                        placeholder={"Select Expiry Date"}
                        component={CustomFlatpickr}
                        options={{
                          altInput: true,
                          altFormat: "j-M-Y",
                          dateFormat: "d-m-Y",
                          minDate: ManufacturerDate,
                        }}
                      />
                    </Col>
                  </Row>

                  <hr className="mt-1 mb-2"></hr>
                  <button type="submit" className="btn btn-primary btn-sm me-2">
                    <i className="fa-solid fa-floppy-disk"></i> Submit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={(e) => {
                      navigate(-1);
                    }}
                  >
                    <i className="fa fa-cancel"></i> Cancel
                  </button>
                </Form>
              </FormikProvider>
            </div>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default AddNDTMaterial;
